'use client';

import { useState, FormEvent } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Lock, Mail, Eye, EyeOff, ArrowRight, ShieldCheck, KeyRound, LayoutDashboard } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Terjadi kesalahan';
      if (
        message.includes('invalid-credential') ||
        message.includes('wrong-password') ||
        message.includes('user-not-found')
      ) {
        setError('Email atau password salah.');
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    'w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm transition-all focus:border-teal-500 focus:ring-4 focus:ring-teal-500/10 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed outline-none placeholder:text-slate-400';

  return (
    <div className="min-h-screen flex">
      {/* Left — Branding Panel */}
      <div className="hidden lg:flex lg:w-[45%] bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-teal-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-teal-600 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <div>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-bold tracking-tight text-white">
                Aksa<span className="text-teal-400">demia</span>
              </span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-teal-400 bg-teal-400/10 border border-teal-400/20 px-2 py-0.5 rounded">
                Admin
              </span>
            </div>
          </div>

          {/* Center content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-white leading-tight mb-3">
                Dashboard<br />Administrator
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed max-w-sm">
                Kelola latihan/ujian, pantau performa siswa, dan atur seluruh platform dari satu tempat.
              </p>
            </div>

            <div className="space-y-4">
              {[
                { icon: LayoutDashboard, text: 'Kelola latihan/ujian & konten' },
                { icon: ShieldCheck, text: 'Kontrol akses & keamanan' },
                { icon: KeyRound, text: 'Pengaturan platform terpusat' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-teal-400/10 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-teal-400" />
                  </div>
                  <span className="text-sm text-slate-300">{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <p className="text-xs text-slate-500" suppressHydrationWarning>
            &copy; {new Date().getFullYear()} Aksademia. Platform latihan ujian terpercaya.
          </p>
        </div>
      </div>

      {/* Right — Login Form */}
      <div className="flex-1 flex flex-col bg-slate-50">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold tracking-tight text-slate-900">
              Aksa<span className="text-teal-600">demia</span>
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-teal-600 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded">
              Admin
            </span>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Masuk</h1>
              <p className="text-sm text-slate-500">Masuk ke dashboard administrator</p>
            </div>

            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm flex items-center gap-2">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="admin@aksademia.com"
                    disabled={isLoading}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Masukkan password"
                    disabled={isLoading}
                    className={`${inputClass} !pr-11`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Masuk
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Akses terbatas untuk administrator</span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
